import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import memoize from 'memoizee';
import { difference, find } from 'lodash'
import config from 'cityads-v3/config'

import { getWmOfferSubscription } from 'cityads-v3/actions/wmOfferSubscription'
import { getWmOfferUrls } from 'cityads-v3/actions/wmOfferUrls'
import { getOfferStat, getFavorites, setFavorite, getOffersByIds, getOfferTags } from 'cityads-v3/actions/offers'
import { loadReward, selectRewardByLink, selectIsNotLoaded, selectRewardIsLoading } from 'cityads-v3/reducers/offerReward'

import { useEntities } from 'cityads-v3/hooks/entities';

import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined'
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteOutlined';

import { mapObjectize } from 'cityads-v3/utils/apiUtils';
import { yandexReachGoal, mindboxNotifyAddProductToList, googleEvent, amplitudeSendEvent } from 'cityads-v3/utils/counterUtils';

import {
  USER_ROLE_CODE_MANAGER,
  USER_ROLE_CODE_WEBMASTER,
} from 'cityads-v3/reducers/auth';

import {
  SUBSCRIPTION_STATUS_ID_PENDING,
  TOOL_ID_FIRANO,
  TOOL_ID_LINKS,
} from 'cityads-v3/reducers/offers';

import {
  REWARD_CALC_DATA,
  OFFERS_WITH_REWARD_CALCULATOR,
} from 'cityads-v3/containers/webmaster/offerPage/Reward.data'

export function useActionLink(id) {
  const { t } = useTranslation(['Offers']);
  const { search, pathname } = useLocation();
  const onClick = useOnConnectClick(id);
  const link = useMemo(() => (
    {
      title: t('exports.getActionLink'),
      isPrimary: true,
      variant: 'contained',
      link: `${pathname}/${id}/getActionLink${search}`,
      onClick
    }
  ), [id, onClick, pathname, search, t]);

  return link;
}

export function useFiranoLink(id) {
  const { t } = useTranslation(['Offers']);
  const link = useMemo(() => (
    {
      title: t('exports.getFiranoLink'),
      isPrimary: true,
      variant: 'contained',
      link: `/publisher/firano?offerId=${id}`
    }
  ), [id, t]);

  return link;
}

export function useRequestLink(id) {
  const { t } = useTranslation(['Offers']);
  const { pathname, search } = useLocation();
  const onClick = useOnConnectClick(id);
  const link = useMemo(() => (
    {
      title: t('exports.connect'),
      isPrimary: true,
      variant: 'contained',
      link: `${pathname}/request/${id}${search}`,
      onClick,
    }
  ), [t, pathname, id, search, onClick]);
  return link;
}

export function useOnConnectClick(id) {
  const dispatch = useDispatch();
  const onConnectClick = useCallback(() => {
    yandexReachGoal('CLICK_OFFER_CONNECT', { offerId: id });
    googleEvent('Offers', 'CLICK_OFFER_CONNECT', id);
    dispatch(mindboxNotifyAddProductToList('Online.GetLink', id));
  }, [dispatch, id]);

  return onConnectClick;
}

export function useOnClickMoreInfo(id) {
  const onClick = useCallback(() => {
    yandexReachGoal('CLICK_OFFER_MORE', { offerId: id });
    googleEvent('Offers', 'CLICK_OFFER_MORE', id);
  }, [id]);
  return onClick;
}

/**
 * Был ли ранее отправлен запрос на получение подписки на оффер
 * @param {number} offerId
 * @returns {boolean}
 */
export function useOfferAccessRequested(offerId) {
  const accessRequested = useSelector(state => state.offers.accessRequested);
  return !!accessRequested[Number(offerId)];
}

const getComponentSubscriptions = memoize(function getComponentSubscriptions(offerSubscriptionIdsByOfferId, offerSubscriptionsById, offerId) {
  return (offerSubscriptionIdsByOfferId[offerId] || []).map(id => offerSubscriptionsById[id]);
});

/**
 * Подписан ли вебмастер на оффер
 * @param {number} offerId
 * @returns {boolean}
 */
export function useOfferConnected(offerId) {
  const subscriptions = useSelector(state => getComponentSubscriptions(state.offers.offerSubscriptionIdsByOfferId, state.offers.offerSubscriptionsById, offerId));
  const activeSubscriptions = subscriptions.filter(item => item.isActiveSubscription);
  return activeSubscriptions.length > 0;
}


/**
 * Получение информации для ссылки "подробнее"
 * @param {number} id
 * @param {string} linkSource
 * @returns {object|null}
 */
export function useMoreInfoAction(id, linkSource = '') {
  const { t } = useTranslation(['Offers']);
  const [link, linkType] = useOfferLink(id, linkSource);
  const clickMoreInfo = useOnClickMoreInfo(id);

  const moreInfoAction = useMemo(() => {
    const actionButton = {
      title: t('moreInfo'),
      onClick : clickMoreInfo,
      component : linkType === TYPE_LINK ? Link : 'a'
    };

    if (linkType === TYPE_LINK) {
      return { ...actionButton, to: link }
    }

    if (linkType === TYPE_URL) {
      return { ...actionButton, href: link }
    }

    return null;
  }, [t, clickMoreInfo, linkType, link]);

  return moreInfoAction;
}

function isLinksToolEnabled(tools = []) {
  return tools && tools.indexOf(TOOL_ID_LINKS) >= 0;
}

/**
 * Есть ли у оффера инструмент "Ссылки"
 * @param {number} offerId
 * @returns {boolean}
 */
export function useIsLinksToolEnabled(offerId) {
  const { tools } = useSelector(state => state.offers.offersById[offerId] ?? {});
  return isLinksToolEnabled(tools);
}

function isFiranoToolEnabled(tools = []) {
  return tools && tools.indexOf(TOOL_ID_FIRANO) >= 0;
}

/**
 * Есть ли у оффера инструмент "Фирано"
 * @param {number} offerId
 * @returns {boolean}
 */
export function useIsFiranoToolEnabled(offerId) {
  const { tools } = useSelector(state => state.offers.offersById[offerId] ?? {});
  return tools && tools.indexOf(TOOL_ID_FIRANO) >= 0;
}

export function useActions(id, customActions, isCoupon) {
  const { subscription_status, tools } = useSelector(state => state.offers[isCoupon ? 'couponsById' : 'offersById'][id] ?? {});
  const isOfferConnected = useOfferConnected(id);
  const isOfferAccessRequested = useOfferAccessRequested(id);
  const { user_role_code } = useSelector(state => state.auth.user ?? {});
  const actionLink = useActionLink(id);
  const firanoLink = useFiranoLink(id);
  const request = useRequestLink(id);
  const moreInfoAction = useMoreInfoAction(id);
  const rewardLink = useRewardLink(id);

  const actions = useMemo(() => (
    customActions || [
      ...((user_role_code === USER_ROLE_CODE_WEBMASTER && subscription_status !== SUBSCRIPTION_STATUS_ID_PENDING) ?
        isOfferConnected ?
          ([
            isLinksToolEnabled(tools) ? actionLink : false,
            isFiranoToolEnabled(tools) ? firanoLink : false
          ])
          :
          (isOfferAccessRequested ? [] : [request])
        :
        []
      ),
      moreInfoAction,
      rewardLink
    ]
  ), [customActions, user_role_code, subscription_status, isOfferConnected, tools, actionLink, firanoLink, isOfferAccessRequested, request, moreInfoAction, rewardLink]);

  return actions;
}

export function useSubActions(id, isCoupon, onFavoriteClick) {
  const isFavorite = useSelector(state => Boolean(state.offers.offerFavoritesByOfferId[id]));
  const favoriteState = useSelector(state => state.offers.offerFavoritesStateByOfferId[id]);
  const { t } = useTranslation(['Offers']);

  const subActions = useMemo(() => (
    [
      ...(isCoupon ? [] : [{
        icon: isFavorite ? <FavoriteOutlinedIcon /> : <FavoriteBorderOutlinedIcon />,
        color: isFavorite ? 'primary' : undefined,
        tooltip: t('addToFavorites'),
        disabled: favoriteState !== 'ready',
        onClick: onFavoriteClick
      }])
    ]
  ), [favoriteState, isCoupon, isFavorite, onFavoriteClick, t]);

  return subActions;
}

export const TYPE_LINK = 'link';
export const TYPE_URL = 'url';
export function useOfferLink(id, linkSource) {
  const linkGetter = useOfferLinkGetter()
  return linkGetter(id, linkSource)
}

export function useOfferLinkGetter() {
  const location = useLocation();
  const user = useSelector(state => state.auth.user);

  return useCallback((id, linkSource) => {
    let linkType
    let link;

    if (!user || user.user_role_code === USER_ROLE_CODE_WEBMASTER) { linkType = TYPE_LINK }
    else if (user.user_role_code === USER_ROLE_CODE_MANAGER) { linkType = TYPE_URL }

    if (linkType === TYPE_LINK) {
      link = {
        pathname: `/publisher/offer/${id}`,
        state: {
          source: linkSource,
          sourceSearch: location.search,
        },
      }
    } else if (linkType === TYPE_URL) {
      link = `//cityads.com/mng_new/offers/offers/${id}`
    }

    return [link, linkType]
  }, [location, user])
}


/**
 * Подгрузка списка недостающих офферов по списку id
 */
export function usePreloadOffers(offerIds = []) {
  const dispatch = useDispatch();
  const skipFirst = useRef(null);
  const offers = useSelector(state => offerIds.map(id => state.offers?.offersById?.[id]))
  const language = useSelector(state => state.language.language)

  const notLoadedIds = useSelector(state => difference(
    offerIds,
    Object.keys(state.offers.offersById).map(Number),
    state.offers.isLoadingOffersByIds,
  ));

  useEffect(() => {
    if (notLoadedIds.length) {
      dispatch(
        getOffersByIds({
          ids: notLoadedIds
        })
      )
    }
  }, [notLoadedIds, dispatch])

  useEffect(() => {
    if (offerIds.length) {
      if (skipFirst.current !== null && language !== skipFirst.current) {
        dispatch(
          getOffersByIds({
            ids: offerIds
          })
        )
      }
      skipFirst.current = language;
    }
  }, [dispatch, language, offerIds])

  return offers;
}


/**
 * Подгрузка подписок на оффер
 * @param {number[]} offerIds
 */
export function useLoadWmOfferSubscription(offerIds = []) {
  const dispatch = useDispatch();
  const userRoleCode = useSelector(state => state.auth.user?.user_role_code);
  const alreadyLoaded = useSelector(state => Object.keys(state.offers.offerSubscriptionIdsByOfferId).map(Number));
  const loadIds = useMemo(() => difference(offerIds, alreadyLoaded).filter(id => !isNaN(+id)), [alreadyLoaded, offerIds]);
  const isLoading = useSelector(state => {
    return loadIds.map(id => state.offers.offerSubscriptionIdsByOfferId[id]).some(item => item?.isLoading)
  });

  useEffect(() => {
    if (userRoleCode === USER_ROLE_CODE_WEBMASTER && !isLoading && loadIds.length) {
      dispatch(getWmOfferSubscription(loadIds))
    }
  }, [dispatch, userRoleCode, isLoading, loadIds]);
}


/**
 * Подгрузка статистики оффера
 */
export function useLoadOfferStat(offerIds = []) {
  const dispatch = useDispatch();
  const alreadyLoaded = useSelector(state => Object.keys(state.offers.offerStatByOfferId).map(Number));
  const loadIds = useMemo(() => difference(offerIds, alreadyLoaded), [alreadyLoaded, offerIds]);
  const isLoading = useSelector(state => {
    return loadIds.map(id => state.offers.offerStatByOfferId[id]).some(item => item?.isLoading)
  });

  useEffect(() => {
    if (!isLoading && loadIds.length) {
      dispatch(getOfferStat(loadIds))
    }
  }, [dispatch, isLoading, loadIds]);
}

/**
 * Получение статы по офферам
 * @param {number[]} offerIds
 * @returns {OfferStatReducer}
 */
export function useOfferStat(offerIds = []) {
  useLoadOfferStat(offerIds)
  return useSelector(state => state.offers.offerStatByOfferId)
}


/**
 * Подгрузка информации по наличию оффера в избранном
 */
export function useLoadFavorites(offerIds = []) {
  const dispatch = useDispatch();
  const alreadyLoaded = useSelector(state => Object.keys(state.offers.offerFavoritesByOfferId).map(Number));
  const loadIds = useMemo(() => difference(offerIds, alreadyLoaded), [alreadyLoaded, offerIds]);
  const isLoading = useSelector(state => {
    return loadIds.map(id => state.offers.offerFavoritesStateByOfferId[id]).some(state => state === 'loading')
  });

  useEffect(() => {
    if (!isLoading && loadIds.length) {
      dispatch(getFavorites(loadIds))
    }
  }, [dispatch, isLoading, loadIds]);
}


/**
 * Действие добавления оффера в избранное
 * @param {number} offerId - id оффера
 * @param {function} defaultCallback - действие при нажатии на кнопку добавления в избранное
 * @param {function} dispatchCallback - действие после добавления в избранное
 */
export function useOnFavoriteClick(offerId, defaultCallback = null, dispatchCallback = () => { }) {
  const dispatch = useDispatch();
  const favoritesByOfferId = useSelector(state => state.offers.offerFavoritesByOfferId)

  const cb = useCallback((eventOrId) => {
    const currentId = typeof eventOrId === 'number' ? eventOrId : offerId
    const isFavorite = favoritesByOfferId[currentId]

    if (typeof eventOrId === 'object') {
      eventOrId?.preventDefault?.()
    }

    dispatch(setFavorite(currentId, !isFavorite))
      .then(dispatchCallback);

    yandexReachGoal('CLICK_OFFER_FAVORITE', { offerId: currentId })
    googleEvent('Offers', 'CLICK_OFFER_FAVORITE', currentId)
  }, [dispatch, dispatchCallback, favoritesByOfferId, offerId]);

  return defaultCallback ?? cb;
}

/**
 * Получение информации калькулятора вознаграждений по офферу
 * @param {number} offerId
 * @returns {IRewardInfo|undefined}
 */
export function useRewardCalcData(offerId) {
  const { t } = useTranslation(['Offers']);
  const rewardData = REWARD_CALC_DATA(t);
  return find(rewardData, {offerId});
}

/**
 * Проверка наличия калькулятора вознаграждений у оффера
 * @param {number} id - offerId
 * @returns {Boolean}
 */
export function useOfferHasRewardCalculator(id) {
  const { subscription_status, tools } = useSelector(state => state.offers.offersById[id] ?? {});
  const { user_role_code } = useSelector(state => state.auth.user ?? {});
  const isOfferConnected = useOfferConnected(id);

  return (
    OFFERS_WITH_REWARD_CALCULATOR.includes(id)
    && user_role_code === USER_ROLE_CODE_WEBMASTER
    && subscription_status !== SUBSCRIPTION_STATUS_ID_PENDING
    && isOfferConnected
    && isLinksToolEnabled(tools)
  )
}

/**
 * Формирование ссылки для калькулятора вознаграждения
 * @param {number} id - offerId
 * @param {string} linkSource
 * @returns {object|null}
 */
export function useRewardLink(id, linkSource = '') {
  const location = useLocation();
  const { t } = useTranslation(['Offers']);
  const offerHasRewardCalculator = useOfferHasRewardCalculator(id);

  const link = useMemo(() => (
    {
      title: t('page.reward'),
      isPrimary: false,
      variant: 'text',
      component: Link,
      to: {
        pathname: `/publisher/offer/${id}/reward`,
        state: {
          source: linkSource,
          sourceSearch: location.search,
        },
      },
      onClick: () => {
        const eventData = { offerId: id };
        yandexReachGoal('REWARD_OFFER_CARD_CLICK', eventData);
        amplitudeSendEvent('makeActions', 'd-v2-e8', 'offers', 'REWARD_OFFER_CARD_CLICK', eventData);
      }
    }
  ), [id, t, linkSource, location]);

  if (offerHasRewardCalculator) {
    return link;
  }

  return null;
}

/**
 * запрос данных по вознаграждению
 * @param {string} link
 */
export function useLoadReward(link) {
  const dispatch = useDispatch();
  const notLoaded = useSelector(selectIsNotLoaded(link));
  const rewardData = useSelector(selectRewardByLink(link));
  const isLoading = useSelector(selectRewardIsLoading(link));
  const abortController = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (notLoaded) {
      if (abortController.current) {
        abortController.current.abort();
      }

      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        abortController.current = new AbortController();
        dispatch(loadReward(link, abortController.current.signal))
      }, config.KEYBOARD_TIMEOUT);
    }
  }, [dispatch, link, notLoaded])

  return {
    rewardData,
    isLoading,
    notLoaded
  };
}

export function useOfferData(id) {
  return useSelector(state => state.offers.offersById[id] ?? {})
}

/**
 * Подгрузка лендингов оффера
 */
export function usePreloadWmOfferUrl(offerId, preload = true) {
  const dispatch = useDispatch();
  const wmOfferUrls = useSelector(state => state.wmOfferUrls.urlsByOfferId[offerId]);

  useEffect(() => {
    if (preload && !wmOfferUrls?.isLoaded && !wmOfferUrls?.isLoading) {
      dispatch(getWmOfferUrls(offerId))
    }
  }, [dispatch, offerId, wmOfferUrls?.isLoaded, wmOfferUrls?.isLoading, preload]);

  return wmOfferUrls?.dataById;
}

/**
 * Подгрузка тегов офферов
 */
export function usePreloadOfferTags() {
  const didMountRef = useRef(false);
  const dispatch = useDispatch();
  const isLoaded = useSelector(state => state.offers.isLoadedOfferTags);
  const language = useSelector(state => state.language.language);

  // Language change
  useEffect(() => {
    if (didMountRef.current) {
      dispatch(getOfferTags());
    }
  }, [dispatch, language]);

  // Initial load
  useEffect(() => {
    if (!didMountRef.current && !isLoaded) {
      dispatch(getOfferTags());
    }

    didMountRef.current = true;
  }, [dispatch, isLoaded]);
}

let promoOffersLoadTimer = null;

/**
 * Подгрузка промо-офферов
 */
export function usePromoOffers() {
  const didMountRef = useRef(false);
  const [promoOffers, isLoading, isLoaded, load] = useEntities('promoOffers');
  const language = useSelector(state => state.language.language);

  const scheduleLoad = useCallback(() => {
    clearTimeout(promoOffersLoadTimer);
    promoOffersLoadTimer = setTimeout(() => load());
  }, [load]);

  // Language change
  useEffect(() => {
    if (didMountRef.current) {
      scheduleLoad();
    }
  }, [scheduleLoad, language]);

  // Initial load
  useEffect(() => {
    if (!didMountRef.current && !isLoaded && !isLoading) {
      scheduleLoad();
    }

    didMountRef.current = true;
  }, [scheduleLoad, isLoaded, isLoading]);

  return useMemo(() => {
    return mapObjectize(
      promoOffers?.filter(({ is_active = true, active_from = null, active_to = null }) =>
        is_active &&
        (!active_from || (new Date(active_from) <= new Date())) &&
        (!active_to || (new Date(active_to) >= new Date()))
      ) || [],
      'offer_id'
    );
  }, [promoOffers]);
}

export function useOfferTags() {
  const tags = useSelector(state => state.offers.tags);
  const isBlogger = useSelector(state => state.auth.user?.isBlogger ?? false);

  const tagsWithVisible = useMemo(() => tags.map(tag => ({
    ...tag,
    isVisible: isBlogger ? tag.is_visible_to_bloggers : tag.is_visible_to_non_bloggers,
  })), [tags, isBlogger]);

  const tagsWithVisibleByCode = useMemo(() => mapObjectize(tagsWithVisible, 'code'), [tagsWithVisible]);

  return [tagsWithVisible, tagsWithVisibleByCode];
}
